import React, { useEffect, useState, useRef } from 'react';
import { ConfettiBackground } from '../components/ThemeEffects';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, QrCode, ArrowRight, DownloadSimple, Share, Play, Microphone, FilmStrip, ChatCircleDots } from '@phosphor-icons/react';
import { EventDropLogo } from '../components/EventDropLogo';
import confetti from 'canvas-confetti';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThankYouPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasUpsell = searchParams.get('upsell') === 'true';
  const sessionId = searchParams.get('session_id');
  const [eventData, setEventData] = useState(null);
  const [videoReady, setVideoReady] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const end = Date.now() + 2500;
    const colors = ['#B89762', '#C29D52', '#B89762', '#B89762'];
    const frame = () => {
      confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors });
      confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();

    // Fetch event data from session
    if (sessionId) {
      const fetchEvent = async () => {
        try {
          const res = await axios.get(`${API}/checkout/status/${sessionId}`);
          if (res.data.event) {
            setEventData(res.data.event);
            // Store event_id for dashboard claiming
            localStorage.setItem('pending_event_id', res.data.event.event_id);
          }
        } catch (e) { /* ignore */ }
      };
      fetchEvent();
    }

    const checkVideo = () => {
      fetch(`${BACKEND_URL}/api/demo-video`)
        .then(res => { if (res.ok) setVideoReady(true); })
        .catch(() => {});
    };
    checkVideo();
    const check = setInterval(checkVideo, 4000);
    return () => clearInterval(check);
  }, [sessionId]);

  const handleGoToEvent = () => {
    // Return to the dashboard on the Events tab with ?purchased=true so the
    // Pricing-tab → Buy-now flow lands users back on a celebratory confetti
    // screen with a prominent "Create your next event" toast action.
    navigate('/my-events?purchased=true');
  };

  const handlePlayVideo = () => {
    setVideoPlaying(true);
    setTimeout(() => { if (videoRef.current) videoRef.current.play(); }, 100);
  };

  const eventName = eventData?.event_name;

  return (
    <div className="min-h-screen bg-[#15161B] text-white flex items-center justify-center px-6" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="max-w-lg w-full py-16" data-testid="thank-you-page">
        <ConfettiBackground />
        {/* Success badge */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-5">
            <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-400 flex items-center justify-center">
              <Check size={32} weight="bold" className="text-green-400" />
            </div>
          </div>
          <h1 className="text-3xl font-black tracking-tight mb-2" data-testid="thank-you-title">
            {eventName ? `${eventName} is ready!` : 'Your event is ready!'}
          </h1>
          {hasUpsell && <p className="text-[#B89762] font-bold mb-1">Photobooth added to your account</p>}
          <p className="text-gray-500 text-base">
            {eventName ? 'Sign in to start customising your event' : 'Watch how it works in 30 seconds'}
          </p>
        </div>

        {/* UNLOCKED TILE — lists everything the customer just activated */}
        {eventData && (() => {
          const UNLOCKED = [
            { flag: 'capture_purchased',            label: 'QR Photo Upload',       pkgOk: ['capture', 'experience', 'legacy'] },
            { flag: 'pocket_booth_purchased',       label: 'Pocket Booth',          pkgOk: ['experience', 'legacy'] },
            { flag: 'booth_purchased',              label: 'Photo Booth',           pkgOk: ['legacy'] },
            { flag: 'guestbook_purchased',          label: 'Video Guestbook',       pkgOk: ['experience', 'legacy'] },
            { flag: 'challenges_purchased',         label: 'Challenge Mode',        pkgOk: ['experience', 'legacy'] },
            { flag: 'invites_purchased',            label: 'Invites',               pkgOk: ['legacy'] },
            { flag: 'highlight_purchased',          label: 'Highlight Reel',        pkgOk: ['capture', 'experience', 'legacy'] },
            { flag: 'film_purchased',               label: 'Event Film',            pkgOk: ['experience', 'legacy'] },
            { flag: 'memory_book_purchased',        label: 'Memory Guest Book',     pkgOk: ['legacy'] },
            { flag: 'has_pro_upgrade',              label: 'Pro (no watermark)',    pkgOk: ['legacy'] },
          ];
          // Prefer per-flag truth; fall back to package membership for bundles where
          // per-product webhook fan-out hasn't landed yet at the point of render.
          const pkg = eventData.package_type;
          const enabled = UNLOCKED.filter(u =>
            eventData[u.flag] || (Array.isArray(u.pkgOk) && u.pkgOk.includes(pkg))
          );
          if (enabled.length === 0) return null;
          return (
            <div
              className="mb-10 rounded-2xl p-5 border"
              style={{ background: 'linear-gradient(135deg, rgba(194,157,82,0.08), rgba(184,151,98,0.06))', borderColor: 'rgba(194,157,82,0.28)' }}
              data-testid="unlocked-tile"
            >
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#C29D52] mb-3">You've unlocked</p>
              <div className="grid grid-cols-2 gap-2">
                {enabled.map(u => (
                  <div key={u.flag} className="flex items-center gap-2 text-sm text-white/90" data-testid={`unlocked-${u.flag}`}>
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #C29D52, #B89762)' }}
                    >
                      <Check size={12} weight="bold" className="text-white" />
                    </span>
                    <span className="truncate">{u.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-white/40 mt-3">All set — explore each one from your event dashboard.</p>
            </div>
          );
        })()}

        {/* QUICK-START VIDEO */}
        {!eventName && (
          <div className="mb-10 rounded-2xl overflow-hidden border border-white/10" data-testid="quickstart-video-section">
            {videoReady ? (
              videoPlaying ? (
                <video ref={videoRef} src={`${BACKEND_URL}/api/demo-video`} controls autoPlay className="w-full aspect-video bg-black" data-testid="quickstart-video-player" />
              ) : (
                <div onClick={handlePlayVideo} className="w-full aspect-video flex items-center justify-center cursor-pointer group relative" style={{ background: 'linear-gradient(135deg, #0D0A1A 0%, #1A0525 50%, #15161B 100%)' }} data-testid="quickstart-video-overlay">
                  <div className="absolute inset-0 flex items-center justify-center opacity-5"><FilmStrip size={200} weight="thin" className="text-white" /></div>
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center border border-white/20 mb-3 group-hover:scale-110 transition-transform" style={{ background: 'rgba(62,198,231,0.2)', backdropFilter: 'blur(12px)' }}>
                      <Play size={28} weight="fill" className="text-white ml-1" />
                    </div>
                    <p className="text-white/70 text-sm font-medium">Watch how it works</p>
                    <p className="text-white/30 text-xs mt-1">30 seconds</p>
                  </div>
                </div>
              )
            ) : (
              <div className="w-full aspect-video flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0D0A1A, #15161B)' }}>
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#B89762]/50 border-t-transparent mx-auto mb-3"></div>
                  <p className="text-white/40 text-sm">Preparing your walkthrough...</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ONBOARDING STEPS */}
        <div className="space-y-4 mb-10">
          {[
            { step: '1', icon: <QrCode size={22} weight="bold" />, title: 'Sign in to your account', desc: 'Create a free account or sign in with Google' },
            { step: '2', icon: <DownloadSimple size={22} weight="bold" />, title: 'Download your QR code', desc: 'Print it or display it on a screen at your venue' },
            { step: '3', icon: <Share size={22} weight="bold" />, title: 'Share with guests', desc: 'Guests scan and start uploading instantly' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-4 rounded-2xl border border-white/10 bg-[#15161B] p-5">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-black" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}>
                {item.step}
              </div>
              <div>
                <h3 className="font-bold mb-0.5 text-sm">{item.title}</h3>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* MAIN CTA */}
        <button
          onClick={handleGoToEvent}
          className="w-full py-5 rounded-2xl text-lg font-bold text-white transition-all hover:scale-[1.02] shadow-lg shadow-[#B89762]/20 flex items-center justify-center gap-3"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          data-testid="create-event-cta"
        >
          {eventName ? `Go to ${eventName}` : 'Continue to Dashboard'}
          <ArrowRight size={20} weight="bold" />
        </button>

        <p className="text-sm text-gray-600 mt-6 text-center">Need help? Contact support@eventdrop.co</p>
      </div>
    </div>
  );
};

export default ThankYouPage;
